import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { readAndCompressImage } from 'browser-image-resizer/src/index';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import { Helmet } from 'react-helmet-async';
import MuiLink from '@mui/material/Link';
import Button from 'components/Button';
import IconButton from 'components/IconButton';
import { Aside, Section, StyledRoot } from '../styles/create-video.styled';
import Spin from 'components/Spin';
import CustomizePage from '../components/create/CustomizePage';
import { saveVideoPage, getVideoPages } from 'services/videoPages.service';
import { uploadMedia } from 'services/media';
import toast, { snack } from 'utils/toast';
import apiClient from 'lib/apiClient';
import pick from 'lodash/pick';
import { BUCKET_URL } from 'constants/index';
import BasicDetails from '../components/create/BasicDetails';
import VideoPreview from '../components/create/VideoPreview';
import ViewIcon from '@mui/icons-material/VisibilityOutlined';
import CopyToClipboard from 'react-copy-to-clipboard';
import LinkIcon from '@mui/icons-material/Link';
import qs from 'qs';
import QRIcon from '@mui/icons-material/QrCode2Outlined';
import PageWrapper from 'components/PageWrapper';
import ShareVideoModal from '../components/ShareVideoModal';
import { COLORS } from 'constants/theme';

const useStyles = makeStyles((theme) => ({
  iconButton: {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    marginLeft: 8,
    padding: 8,
  },
}));

const DEFAULT_COLOR = '2186eb';

const CreateVideo = ({
  history,
  match: {
    params: { id },
  },
}) => {
  const account = useSelector((s) => s.settings.account);
  const [videoPage, setVideoPage] = useState();
  const [values, setValues] = useState({
    headline_color: '#000000',
    design_template: '1',
    is_message_reaction_enabled: '1',
    call_to_action_enabled: '1',
    is_popup_view: '1',
    template_color: account.account_color
      ? '#' + account.account_color
      : COLORS.TIC,
    call_to_action_text: 'Button Text',
  });

  const [uploadProgress, setUploadProgress] = useState(0);
  const [shareOptionsModal, setShareOptionsModal] = useState();
  const classes = useStyles();

  useEffect(() => {
    if (id) {
      getVideoPages({
        hash: id,
      }).then((resp) => {
        setVideoPage(resp[0]);
      });
    }
  }, [id]);

  useEffect(() => {
    if (videoPage) {
      const newValues = pick(videoPage, [
        'headline',
        'message',
        'headline_color',
        'video_source',
        'call_to_action_text',
        'call_to_action',
        'call_to_action_enabled',
        'thumbnail_url',
        'design_template',
        'video_url',
        'is_message_reaction_enabled',
        'is_popup_view',
        'link_to',
        'template_color',
        'file_id',
        'logo',
      ]);

      if (newValues.video_url) {
        newValues.enableVideo = '1';
      }

      setValues(newValues);
    }
  }, [videoPage]);

  const onChange = (key, value) => {
    setValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const uploadVideo = async (blob) => {
    const config = {
      onUploadProgress: function (progressEvent) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        setUploadProgress(percentCompleted);
      },
    };
    const id = Date.now();
    let fd = new FormData();
    fd.append('media_file', blob, 'recorded-media-' + id + '.webm');

    try {
      const videoResp = await apiClient.post(
        'API/1_0/postMedia.php',
        fd,
        config,
      );
      const url = BUCKET_URL + videoResp.location;
      return url;
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onRemove = () => {
    setValues((prev) => ({
      ...prev,
      video_url: null,
      video: null,
    }));
  };

  const onSave = async () => {
    try {
      if (values.cover) {
        const resized = await readAndCompressImage(values.cover, {
          maxWidth: 900,
          mimeType: values.cover.type,
        });
        const imgResp = await uploadMedia(
          resized,
          account.account_id,
          values.cover.name,
        );
        values.thumbnail_url = BUCKET_URL + imgResp.location;
      }
      if (values.logo) {
        values.file_id = values.logo.file_id;
      } else {
        values.file_id = '';
      }
      if (values.video) {
        values.video_url = await uploadVideo(values.video);
      }
      const resp = await saveVideoPage({
        ...values,
        hash: id,
      });

      snack.success('Page saved.');
      setShareOptionsModal(true);
      setUploadProgress(0);
      if (!id) {
        history.push(`/pages/edit/${resp.hash}`);
      } else {
        getVideoPages({
          hash: id,
        }).then((resp) => {
          setVideoPage(resp[0]);
        });
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const isInvalid = () => {
    let invalid = false;
    if (!values.headline || !values.message) return true;
    if (values.enableVideo === '1') {
      invalid = !values.video_url && !values.video;
    }
    if (values.call_to_action_enabled === '1') {
      invalid = !values.call_to_action_text || !values.call_to_action;
    }
    return invalid;
  };

  if (id && !videoPage) return <Spin spinning />;

  const videoUrl = videoPage?.redirect && videoPage.redirect.qr_code_text;
  return (
    <PageWrapper
      title={id ? 'Edit Page' : 'Create Page'}
      breadcrumbs={[
        { title: 'Pages', path: '/pages' },
        { title: id ? 'Edit Page' : 'Create Page' },
      ]}
      actions={
        <>
          {id && (
            <Box ml={'auto'}>
              <IconButton
                title="Preview"
                onClick={() => window.open(videoUrl)}
                className={classes.iconButton}
                size="small"
                icon={<ViewIcon fontSize="inherit" />}
              />
              <CopyToClipboard
                text={videoUrl}
                onCopy={() => snack.success('Link copied!')}
              >
                <IconButton
                  title="Copy link"
                  className={classes.iconButton}
                  size="small"
                  icon={<LinkIcon fontSize="inherit" />}
                />
              </CopyToClipboard>
              <MuiLink
                target="_blank"
                href={`/api/qr?${qs.stringify({
                  text: videoUrl,
                  color: account.account_color,
                  logo:
                    account.account_logo_url &&
                    `${BUCKET_URL}${account.account_logo_url}`,
                })}`}
              >
                <IconButton
                  title="Download QR Code"
                  className={classes.iconButton}
                  size="small"
                  icon={<QRIcon fontSize="inherit" />}
                />
              </MuiLink>
            </Box>
          )}
        </>
      }
    >
      <Helmet title={id ? 'Edit Page' : 'Create Page'} />
      <StyledRoot color={account.account_color || DEFAULT_COLOR}>
        <Box
          display="flex"
          sx={{
            flex: 1,
            marginBottom: '16px',
            position: 'relative',
          }}
        >
          <Aside p={2}>
            <CustomizePage
              values={values}
              onChange={onChange}
              onRemove={onRemove}
              uploadProgress={uploadProgress}
            />
            <BasicDetails value={values} onChange={onChange} />
            <Box mt={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={onSave}
                disabled={isInvalid()}
              >
                Publish
              </Button>
            </Box>
          </Aside>
          <Section>
            <VideoPreview
              values={values}
              onChange={onChange}
              onRemove={onRemove}
            />
          </Section>
        </Box>
        {shareOptionsModal && (
          <ShareVideoModal
            videoUrl={videoUrl}
            onClose={() => setShareOptionsModal(false)}
          />
        )}
      </StyledRoot>
    </PageWrapper>
  );
};

export default CreateVideo;
